'use client';
import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, XCircle } from 'lucide-react';
import ConfirmModal from '@/components/ui/ConfirmModal';
import {
  deleteAllClaimItems,
  fetchClaimItems,
  deleteClaim,
  fetchRooms,
  fetchFamilyMembers,
  setActiveClaim,
} from '@/lib/claimService';
import { createClient } from '@/lib/supabase/client';
import { useClaimContext } from '@/contexts/ClaimContext';
import { toast } from 'sonner';

export default function DangerZoneSection() {
  const { activeClaim, claims, refreshClaims } = useClaimContext();
  const [clearOpen, setClearOpen] = useState(false);
  const [deleteClaimOpen, setDeleteClaimOpen] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const [roomCount, setRoomCount] = useState(0);
  const [memberCount, setMemberCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!activeClaim?.id) return;
    async function loadCounts() {
      const [items, rooms, members] = await Promise.all([
        fetchClaimItems(activeClaim!.id),
        fetchRooms(activeClaim!.id),
        fetchFamilyMembers(activeClaim!.id),
      ]);
      setItemCount(items?.length ?? 0);
      setRoomCount(rooms?.length ?? 0);
      setMemberCount(members?.length ?? 0);
    }
    loadCounts();
  }, [activeClaim?.id]);

  const handleClearAll = async () => {
    if (!activeClaim?.id) return;
    const ok = await deleteAllClaimItems(activeClaim.id);
    setClearOpen(false);
    if (ok) {
      setItemCount(0);
      toast?.success('All claim items have been cleared. Claim settings are preserved.');
    } else {
      toast?.error('Failed to clear data. Please try again.');
    }
  };

  const handleDeleteClaim = async () => {
    if (!activeClaim?.id) return;
    setIsDeleting(true);
    const supabase = createClient();

    try {
      // Delete all related data in order
      await deleteAllClaimItems(activeClaim.id);
      await supabase.from('ct_rooms').delete().eq('claim_id', activeClaim.id);
      await supabase.from('ct_family_members').delete().eq('claim_id', activeClaim.id);

      // Switch to another claim before deleting this one
      const otherClaim = claims.find((c) => c.id !== activeClaim.id);
      if (otherClaim) {
        await setActiveClaim(otherClaim.id);
      }

      const ok = await deleteClaim(activeClaim.id);
      setDeleteClaimOpen(false);

      if (ok) {
        await refreshClaims();
        toast?.success(`Claim "${activeClaim.name}" and all its data have been permanently deleted.`);
      } else {
        toast?.error('Failed to delete the claim. Please try again.');
      }
    } catch (err) {
      console.error('handleDeleteClaim error:', err);
      toast?.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const claimDisplayName = activeClaim?.name || activeClaim?.claimNumber || 'this claim';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle size={16} className="text-negative" />
        <h2 className="text-base font-semibold text-negative">Danger Zone</h2>
      </div>

      <div className="border border-negative/30 rounded-xl p-4 bg-negative-bg/30 space-y-4">
        {/* Clear All Claim Data */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Clear All Claim Items</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Permanently delete all {itemCount} documented items and price research for{' '}
              <span className="font-medium text-foreground">{claimDisplayName}</span>. Claim settings, rooms, and members are preserved.
            </p>
          </div>
          <button
            onClick={() => setClearOpen(true)}
            disabled={!activeClaim}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-negative border border-negative/30 rounded-lg bg-card hover:bg-negative-bg transition-all duration-150 active:scale-95 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 size={14} />
            Clear All Items
          </button>
        </div>

        <div className="border-t border-negative/20" />

        {/* Delete This Claim */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Delete This Claim</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Permanently delete <span className="font-medium text-foreground">{claimDisplayName}</span> along with all{' '}
              {itemCount} items, {roomCount} rooms, and {memberCount} family members. This cannot be undone.
            </p>
          </div>
          <button
            onClick={() => setDeleteClaimOpen(true)}
            disabled={!activeClaim || isDeleting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-negative border border-negative rounded-lg hover:bg-negative/90 transition-all duration-150 active:scale-95 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle size={14} />
            Delete Claim
          </button>
        </div>
      </div>

      {/* Clear items modal */}
      <ConfirmModal
        open={clearOpen}
        title="Clear all claim items?"
        description={`This will permanently delete all ${itemCount} documented items and their price research for "${claimDisplayName}". Your claim settings, policy number, rooms, and family members will be preserved. This action cannot be undone.`}
        confirmLabel="Yes, Clear All Items"
        cancelLabel="Keep My Data"
        variant="danger"
        onConfirm={handleClearAll}
        onCancel={() => setClearOpen(false)}
      />

      {/* Delete claim modal */}
      <ConfirmModal
        open={deleteClaimOpen}
        title={`Delete "${claimDisplayName}"?`}
        description={`This will permanently delete the entire claim including all ${itemCount} items, ${roomCount} rooms, ${memberCount} family members, and all claim information. This action cannot be undone and all data will be lost forever.`}
        confirmLabel="Yes, Delete This Claim"
        cancelLabel="Keep This Claim"
        variant="danger"
        onConfirm={handleDeleteClaim}
        onCancel={() => setDeleteClaimOpen(false)}
      />
    </div>
  );
}